import React, { useState } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import InputFile from '@app/components/input-file/InputFile';
import { Post } from '@app/utils/httpProvider';
import { POPUP_API } from '@app/utils/apiUrl';
import { useMutation } from 'react-query';
import DatePicker from 'react-datepicker';
import {
    messageError,
    messageToast,
    status200,
    acceptImage
} from '../../utils/define';
import { dateTimeToUTCRequest } from '../../utils/helper';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

// Khớp với enum PopupDisplayMode bên API: Unlimited = 1, LimitBySessionCount = 2
const DISPLAY_MODE = {
    UNLIMITED: 1,
    LIMIT_BY_SESSION_COUNT: 2
};

// Chuẩn hóa displayMode: API có thể trả về enum dạng chuỗi tên
const toDisplayMode = (value) => {
    if (typeof value === 'string') {
        const key = value.trim().toLowerCase();
        if (key === 'unlimited') return DISPLAY_MODE.UNLIMITED;
        if (key === 'limitbysessioncount')
            return DISPLAY_MODE.LIMIT_BY_SESSION_COUNT;
    }
    return Number(value) === DISPLAY_MODE.LIMIT_BY_SESSION_COUNT
        ? DISPLAY_MODE.LIMIT_BY_SESSION_COUNT
        : DISPLAY_MODE.UNLIMITED;
};

// Chuỗi rỗng / null từ input number không được coi là NaN -> tránh lỗi định dạng
const emptyToUndefined = (value, original) =>
    original === '' || original === null || original === undefined
        ? undefined
        : value;

const schema = yup.object().shape({
    name: yup.string().required(messageError.emptyMessage),
    imagePc: yup.string().required(messageError.emptyMessage),
    endDate: yup
        .date()
        .min(
            yup.ref('startDate'),
            'Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu'
        ),
    maxSessionCount: yup.number().when('displayMode', {
        is: (val) =>
            toDisplayMode(val) === DISPLAY_MODE.LIMIT_BY_SESSION_COUNT,
        then: yup
            .number()
            .transform(emptyToUndefined)
            .min(1, 'Số phiên tối đa phải >= 1')
            .required(messageError.emptyMessage),
        otherwise: yup
            .number()
            .transform(emptyToUndefined)
            .notRequired()
    })
});

const PopupAdd = () => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const addFunc = useMutation((value) =>
        Post(POPUP_API.CREATE_POPUP, null, value, false)
    );

    const handleCreate = async (values) => {
        const model = { ...values };
        model.displayMode = toDisplayMode(values.displayMode);
        model.orderNumber = Number(values.orderNumber);
        const parsedMax = Number(values.maxSessionCount);
        model.maxSessionCount = Number.isFinite(parsedMax) ? parsedMax : 0;
        if (
            model.displayMode === DISPLAY_MODE.LIMIT_BY_SESSION_COUNT &&
            model.maxSessionCount < 1
        ) {
            model.maxSessionCount = 1;
        }
        model.startDate = dateTimeToUTCRequest(values.startDate);
        model.endDate = dateTimeToUTCRequest(values.endDate);
        addFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastCreateError);
                } else {
                    setIsLoading(true);
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.goBack();
                }
            },
            onError: () => {
                toast.error('Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới Popup</Card.Header>
            <Card.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        name: '',
                        imagePc: '',
                        imageMobile: '',
                        linkRedirect: '',
                        startDate: new Date(),
                        endDate: new Date(),
                        orderNumber: 0,
                        isActive: true,
                        displayMode: DISPLAY_MODE.UNLIMITED,
                        maxSessionCount: 3
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        values,
                        errors,
                        setFieldValue
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Tên Popup *
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        placeholder="Tên Popup"
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Link điều hướng
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        placeholder="https://..."
                                        type="text"
                                        name="linkRedirect"
                                        value={values.linkRedirect}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Ảnh PC *
                                </Form.Label>
                                <Col sm="10">
                                    <InputFile
                                        name="imagePc"
                                        accept={acceptImage}
                                        onChange={(files) => {
                                            if (files.length > 0) {
                                                setFieldValue(
                                                    'imagePc',
                                                    files[0].filePath,
                                                    true
                                                );
                                            } else {
                                                setFieldValue(
                                                    'imagePc',
                                                    '',
                                                    true
                                                );
                                            }
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: 'block',
                                            color: '#dc3545'
                                        }}
                                        className="invalid-feedback"
                                    >
                                        {errors.imagePc}
                                    </div>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Ảnh Mobile
                                </Form.Label>
                                <Col sm="10">
                                    <InputFile
                                        name="imageMobile"
                                        accept={acceptImage}
                                        onChange={(files) => {
                                            if (files.length > 0) {
                                                setFieldValue(
                                                    'imageMobile',
                                                    files[0].filePath,
                                                    true
                                                );
                                            } else {
                                                setFieldValue(
                                                    'imageMobile',
                                                    '',
                                                    true
                                                );
                                            }
                                        }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Ngày bắt đầu hiệu lực
                                </Form.Label>
                                <Col sm="10" className="icheck-primary">
                                    <DatePicker
                                        selected={values.startDate}
                                        onChange={(date) => {
                                            setFieldValue(
                                                'startDate',
                                                date,
                                                true
                                            );
                                        }}
                                        showYearDropdown="true"
                                        timeInputLabel="Time:"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        showTimeInput
                                        className="form-control"
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Ngày kết thúc hiệu lực
                                </Form.Label>
                                <Col sm="10" className="icheck-primary">
                                    <DatePicker
                                        selected={values.endDate}
                                        onChange={(date) => {
                                            setFieldValue(
                                                'endDate',
                                                date,
                                                true
                                            );
                                        }}
                                        showYearDropdown="true"
                                        timeInputLabel="Time:"
                                        dateFormat="dd/MM/yyyy HH:mm"
                                        showTimeInput
                                        className="form-control"
                                    />
                                    <div
                                        style={{
                                            display: 'block',
                                            color: '#dc3545'
                                        }}
                                        className="invalid-feedback"
                                    >
                                        {errors.endDate}
                                    </div>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Chế độ hiển thị
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        as="select"
                                        name="displayMode"
                                        value={values.displayMode}
                                        onChange={handleChange}
                                    >
                                        <option value={DISPLAY_MODE.UNLIMITED}>
                                            Không giới hạn (hiện ở mọi phiên)
                                        </option>
                                        <option
                                            value={
                                                DISPLAY_MODE.LIMIT_BY_SESSION_COUNT
                                            }
                                        >
                                            Giới hạn số phiên hiển thị
                                        </option>
                                    </Form.Control>
                                </Col>
                            </Form.Group>
                            {toDisplayMode(values.displayMode) ===
                            DISPLAY_MODE.LIMIT_BY_SESSION_COUNT ? (
                                <Form.Group as={Row} className="mb-3">
                                    <Form.Label className="col-sm-2 col-form-label">
                                        Số phiên tối đa hiển thị
                                    </Form.Label>
                                    <Col sm="10">
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            name="maxSessionCount"
                                            placeholder="3"
                                            value={values.maxSessionCount}
                                            onChange={handleChange}
                                            isInvalid={!!errors.maxSessionCount}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.maxSessionCount}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            Popup chỉ hiển thị trong số phiên
                                            đầu tiên này (mỗi phiên 1 lần).
                                        </Form.Text>
                                    </Col>
                                </Form.Group>
                            ) : null}
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Sắp xếp
                                </Form.Label>
                                <Col sm="10">
                                    <Form.Control
                                        type="number"
                                        name="orderNumber"
                                        placeholder="Sắp xếp"
                                        value={values.orderNumber}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Trạng thái kích hoạt
                                </Form.Label>
                                <Col sm="10" className="icheck-primary">
                                    <Form.Control
                                        id="checkboxPrimary1"
                                        type="checkbox"
                                        placeholder="Trạng thái"
                                        name="isActive"
                                        checked={values.isActive}
                                        onClick={handleChange}
                                    />
                                    <Form.Label htmlFor="checkboxPrimary1">
                                        {' '}
                                    </Form.Label>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button
                                            type="submit"
                                            theme="primary"
                                            disabled={isLoading}
                                        >
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button
                                            onClick={() => history.goBack()}
                                            theme="secondary"
                                        >
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                <ReactSpinner loadings={isLoading} />
            </Card.Body>
        </Card>
    );
};

export default PopupAdd;
